/**
 * From the tool results of one answer to the things a map can draw.
 *
 * Sources (verified shapes, see test/fixtures):
 *   resolve_location / nearby  → node.location.latitude/longitude, node.name, node.ids[].value
 *   search_place               → coord.lat/lon, name, ids[], place_type
 *   search_tourism / expand_kg_pois / get_tourism_details → pois[].coord, name, address, media[],
 *                                  source (`niedersachsen-hub` | `osm`), hub_detail_url (Sprint 749,
 *                                  only on Hub POIs, HEAD-checked upstream — copied, never built)
 *   show_connections / connections → journeys[].legs[].nodes[] (ids + names; coordinates only
 *                                  when the answer was asked with `extended: true`) + pathOnMap,
 *                                  pathSource (`geo` | `otp`), pathColour — the latter two since Sprint 748
 *   show_departures / departures → the board's stop (coordinates via resolve_location if present)
 *   meta-open-data (destination.one Open Data, et4 JSON) → items[].global_id, title, geo.main,
 *                                  texts[rel], media_objects[], street/zip/city, web — the Hub's
 *                                  data through the open door; no hub_detail_url there (see README).
 *                                  A Tour (TouristTrip) carries its course in geo.geometry.value as
 *                                  "lat lon lat lon …" — the Heide-Shuttle rings, hiking routes.
 *   line_course (the sold line tool) → lines[].courses[].pathOnMap[{latitude,longitude}] + lineNumber,
 *                                  name, operator, headsign — the drawn course of a transit line, in
 *                                  the text channel (structuredContent is dropped by OpenWebUI).
 *
 * Nothing is invented: a leg node without a coordinate is looked up among the stops the SAME
 * answer resolved (matching on the DHID's station part), and stays without one otherwise.
 */
import type { MapLeg, MapLegNode, MapModel, MapPin, MapTrack, PinFact, PinLink, RouteLeg, ToolResult } from './model';
import { emptyModel } from './model';
import { num, parseToon, str, walk, type ToonValue } from './toon';

type Obj = { [k: string]: ToonValue };

// An extended answer's intermediate halts arrive with the router's feed prefix (`GTFS.de:de:…`,
// seen 04.09.2026 — a backend slip, ids are meant to be bare); strip it so the station part lines up.
const stationKey = (dhid: string): string => dhid.replace(/^GTFS\.de:/, '').replace(/(_G)+$/, '').split(':').slice(0, 3).join(':');

/** A ride on a line (train, tram, bus …) — not a walk or an individual ride by bike, car or scooter. */
const isTransit = (l: Pick<MapLeg, 'mode'>): boolean => !/^(walk|bike|car|scooter)$/.test(l.mode);

export function extractMap(results: ToolResult[], answerText = ''): MapModel {
  const model = emptyModel();
  const hay = norm(answerText);
  const stopsByStation = new Map<string, MapPin>();
  // stops that are merely resolve_location *candidates* (the model asked for "Campestraße" and got
  // Wolfenbüttel AND Braunschweig back) — kept only when the answer's journeys actually use them
  const candidates = new Set<string>();

  const byId = new Map<string, MapPin>();
  const addPin = (p: MapPin): void => {
    // Stops collapse onto their STATION: a `nearby` around Kröpcke returns every platform of every
    // station (52 pins for one square) — one pin per station is what a map reader wants, and the
    // platform detail lives in the answer text. POIs and places collapse onto the same NAME nearby
    // (a museum from OpenStreetMap and from the Hub) or the same id (the same record through two
    // doors) — never onto a coordinate alone: three tours start at one trailhead, and the
    // Landesmuseum shares its point with the castle it is housed in.
    const have = byId.get(p.id) ?? (p.kind === 'stop' && p.ref ? stopsByStation.get(stationKey(p.ref)) : model.pins.find((q) => q.kind === p.kind && samePlace(q, p)));
    if (have) {
      // one id, two spellings ("Wolfenbütteler Straße 98A" from the geocoder, "Wolfenbütteler Straße"
      // from the street lookup): the spelling the answer uses is the place the reader looks for
      if (have.id === p.id && mentions(p.name, hay) && !mentions(have.name, hay)) {
        Object.assign(have, { name: p.name, lat: p.lat, lon: p.lon, address: p.address ?? have.address, description: p.description ?? have.description });
      }
      // the same POI from two tools (search, then details; OpenStreetMap, then the Hub): the later
      // one fills what the first lacked — typically the details' hub link, image or opening hours
      for (const k of ['address', 'description', 'openingHours', 'price', 'image', 'hubUrl'] as const) {
        if (have[k] === undefined && p[k] !== undefined) have[k] = p[k];
      }
      // the record that brings the entity page names the source ("Niedersachsen-Hub", not "OpenStreetMap")
      if (p.hubUrl && have.hubUrl === p.hubUrl && p.source) have.source = p.source;
      have.facts = [...(have.facts ?? []), ...(p.facts ?? []).filter((f) => !have.facts?.some((g) => g.label === f.label))].slice(0, MAX_FACTS);
      have.links = [...(have.links ?? []), ...(p.links ?? []).filter((l) => !have.links?.some((m) => m.url === l.url))];
      return;
    }
    byId.set(p.id, p);
    model.pins.push(p);
    if (p.kind === 'stop' && p.ref) stopsByStation.set(stationKey(p.ref), p);
  };

  const parsed: { name: string; value: ToonValue }[] = [];
  for (const r of results) {
    const text = r.text.trim();
    if (!text) continue;
    // meta-open-data answers in JSON, our own servers in TOON — and a TOON root
    // array also starts with `[` (`[3]:`), so JSON is a first attempt, not a test.
    let value: ToonValue | undefined;
    try {
      value = JSON.parse(text) as ToonValue;
    } catch {
      try {
        value = parseToon(text);
      } catch {
        value = undefined; /* unreadable answer — skip, never guess */
      }
    }
    if (value !== undefined) parsed.push({ name: shortName(r.name), value });
  }

  // the stops a departures board is for, and the towns the answer looked up (search_place): both
  // decide below what a lookup hit or a POI is worth on the map
  const boardStations = new Set<string>();
  const townLookups = new Set<string>();
  for (const { name, value } of parsed) {
    if (/departures/.test(name)) {
      walk(value, (o, path) => {
        const id = path[0] === 'board' && path.length === 2 ? mainId(o) : null;
        if (id) boardStations.add(stationKey(id));
      });
    }
    if (name === 'search_place') walk(value, (o) => { const nm = str(o.name); if (nm) townLookups.add(norm(nm)); });
  }

  // 1. stops and places first, so legs can borrow their coordinates
  for (const { name, value } of parsed) {
    if (name === 'resolve_location' || name === 'nearby' || name === 'stops') {
      walk(value, (o) => {
        const node = (o.node as Obj) ?? (isStopNode(o) ? o : null);
        if (!node) return;
        const loc = node.location as Obj | undefined;
        const lat = num(loc?.latitude);
        const lon = num(loc?.longitude);
        const nm = str(node.name);
        if (lat === null || lon === null || !nm) return;
        // `type` decides: a stop is a stop; an address/locality/poi hit is a place. "Campestraße"
        // as an address query returned Braunschweig, Berlin, Leipzig and Trittau — none a stop.
        const isStop = (str(node.type) ?? 'stop') === 'stop';
        const extras = { facts: factsOf([o, node]), links: linksOf([o, node]) };
        const pin: MapPin = isStop
          ? { id: `stop-${mainId(node) ?? `${lat},${lon}`}`, kind: 'stop', name: nm, lat, lon, ref: mainId(node) ?? undefined, address: str(node.areaName) ?? undefined, ...extras }
          : { id: `place-${mainId(node) ?? `${lat},${lon}`}`, kind: 'place', name: nm, lat, lon, ref: mainId(node) ?? undefined, address: str(node.areaName) ?? undefined, ...extras };
        addPin(pin);
        if (name === 'resolve_location') candidates.add(pin.id);
      });
    }
    if (name === 'search_place' || name === 'reverse_geocode') {
      walk(value, (o) => {
        const coord = o.coord as Obj | null | undefined;
        const nm = str(o.name);
        const lat = num(coord?.lat);
        const lon = num(coord?.lon);
        if (!nm || lat === null || lon === null) return;
        const pin: MapPin = { id: `place-${mainId(o) ?? nm}`, kind: 'place', name: nm, lat, lon, ref: mainId(o) ?? undefined, description: str(o.place_type) ?? undefined, facts: factsOf([o]), links: linksOf([o]) };
        addPin(pin);
        // a place lookup returns every namesake ("Wolfenbüttel" the town, the suburb — and a hamlet
        // near Kiel); with a journey in the answer they are disambiguation, not destinations
        if (name === 'search_place') candidates.add(pin.id);
      });
    }
  }

  // 2. POIs
  for (const { name, value } of parsed) {
    if (!/tourism|poi|kg_pois|details/.test(name)) continue;
    walk(value, (o) => {
      const coord = o.coord as Obj | null | undefined;
      const nm = str(o.name);
      const lat = num(coord?.lat);
      const lon = num(coord?.lon);
      if (!nm || lat === null || lon === null) return;
      const media = Array.isArray(o.media) ? (o.media as ToonValue[]).map((m) => str(m)).filter((m): m is string => !!m) : [];
      addPin({
        id: `poi-${str(o.id) ?? `${lat},${lon}`}`,
        kind: 'poi',
        name: nm,
        lat,
        lon,
        address: str(o.address) ?? undefined,
        description: str(o.description) ?? undefined,
        openingHours: str(o.opening_hours) ?? undefined,
        price: str(o.price) ?? undefined,
        image: media[0],
        source: str(o.source) ?? undefined,
        hubUrl: httpsUrl(str(o.hub_detail_url) ?? str(o.hubDetailUrl)),
        facts: factsOf([o]),
        links: linksOf([o]),
      });
    });
  }

  // 2c. curated tour course (get_tourism_details): properties.path_on_map[{lat,lon}] → a drawn track
  //     per tour. Only the DETAIL path carries the snapped course (the search carries the axes but no
  //     line); one detail is one tour, so no "too many lines" guard is needed. The pin above already
  //     carries name + facts + hub link; this adds the line to it. Point form is `{lat, lon}` here —
  //     the tour writes its coord that way — not `{latitude, longitude}` like a leg's `pathOnMap`.
  for (const { name, value } of parsed) {
    if (!/details/.test(name)) continue;
    walk(value, (o) => {
      const pom = o.path_on_map;
      if (!Array.isArray(pom) || pom.length < 2) return;
      const nm = str(o.name) ?? str(o.title);
      const path: [number, number][] = (pom as Obj[])
        .map((p) => [num(p.lon) ?? NaN, num(p.lat) ?? NaN] as [number, number])
        .filter((xy) => !Number.isNaN(xy[0]) && !Number.isNaN(xy[1]));
      if (path.length < 2) return; // nothing to draw — and never reconstruct one
      const id = `tour-${str(o.id) ?? nm ?? `${path[0]![0]},${path[0]![1]}`}`;
      if (!model.tracks.some((t) => t.id === id)) model.tracks.push({ id, name: nm ?? 'Tour', path, source: 'niedersachsen-hub', ref: str(o.id) ?? undefined });
    });
  }

  // 2d. a sketch of a curated tour's course in the search (search_tourism: pois[].path_polyline, the
  //     first five Hub tours): an encoded polyline (Google format, lat first, 1e-5), at most 40 of the
  //     course's own points. The model often answers from the search alone, without a detail — the
  //     sketch is what lets the map draw those tours. A detail's full path_on_map (2c) wins: same id.
  for (const { name, value } of parsed) {
    if (!/tourism|poi/.test(name) || /details/.test(name)) continue;
    walk(value, (o) => {
      const enc = str(o.path_polyline);
      if (!enc) return;
      const path = decodePolyline(enc);
      if (path.length < 2) return; // nothing to draw — and never reconstruct one
      const nm = str(o.name) ?? str(o.title);
      const id = `tour-${str(o.id) ?? nm ?? `${path[0]![0]},${path[0]![1]}`}`;
      if (!model.tracks.some((t) => t.id === id)) model.tracks.push({ id, name: nm ?? 'Tour', path, source: 'niedersachsen-hub', ref: str(o.id) ?? undefined });
    });
  }

  // 2b. destination.one open data (et4 shape) — recognised by shape, not by tool name: the open
  //     server has several tools (GetDetails, Search, …) and OpenWebUI hands us the bare name
  const openData = new Set<(typeof parsed)[number]>(); // handled here → the generic pass skips them
  for (const entry of parsed) {
    const { name, value } = entry;
    const items = (value as Obj | null)?.items;
    if (!Array.isArray(items)) continue;
    for (const raw of items as ToonValue[]) {
      const it = raw as Obj;
      if (typeof it !== 'object' || it === null || !('global_id' in it || 'geo' in it)) continue;
      const geo = ((it.geo as Obj | undefined)?.main as Obj | undefined) ?? undefined;
      const lat = num(geo?.latitude);
      const lon = num(geo?.longitude);
      const nm = str(it.title) ?? str(it.name);
      if (!nm || lat === null || lon === null) continue;
      const texts = Array.isArray(it.texts) ? (it.texts as Obj[]) : [];
      const text = (rel: string): string | undefined => str(texts.find((t) => t.rel === rel && (t.type === 'text/plain' || t.type === undefined))?.value) ?? undefined;
      const media = Array.isArray(it.media_objects) ? (it.media_objects as Obj[]) : [];
      const image = str(media.find((m) => /^image\//.test(str(m.type) ?? ''))?.url) ?? undefined;
      const address = [str(it.street), [str(it.zip), str(it.city)].filter(Boolean).join(' ')].filter(Boolean).join(', ') || undefined;
      addPin({
        id: `poi-${str(it.global_id) ?? str(it.id) ?? `${lat},${lon}`}`,
        kind: 'poi', name: nm, lat, lon, address,
        description: text('details') ?? text('teaser'),
        openingHours: text('openings'),
        price: text('PRICE_INFO'),
        image,
        source: 'destination.one',
        ref: str(it.global_id) ?? undefined,
        facts: factsOf([it]),
        links: linksOf([it]),
      });
      openData.add(entry); // claimed only when this shape actually produced a pin
      const course = str(((it.geo as Obj | undefined)?.geometry as Obj | undefined)?.value);
      const path = course ? parseLatLonString(course) : [];
      if (path.length >= 3) {
        const track: MapTrack = { id: `track-${str(it.global_id) ?? str(it.id) ?? nm}`, name: nm, path, source: 'destination.one', ref: str(it.global_id) ?? undefined };
        if (!model.tracks.some((t) => t.id === track.id)) model.tracks.push(track);
      }
      void name;
    }
  }

  // 2c. line_course (the sold line tool): lines[].courses[].pathOnMap → one drawn track per course.
  //     The geometry rides in the TEXT channel — OpenWebUI drops structuredContent — thinned upstream
  //     to what a text carries (~50 points a course). Each course is drawn like a tour ("HS 2").
  for (const { name, value } of parsed) {
    if (name !== 'line_course') continue;
    const lines = (value as Obj).lines;
    if (!Array.isArray(lines)) continue;
    // The map follows the answer: an ambiguous number matches dozens of lines across the country
    // ("100" → 42 lines) and the text then asks the user to narrow down — drawing them all would
    // flood the map with lines nobody asked for. So a wide match draws nothing; only a focused
    // result is drawn. (The gateway will also stop returning full geometry for a wide match — this
    // is the widget-side guard that holds until it does.)
    const matched = num((value as Obj).matchedTotal) ?? lines.length;
    if (matched > 8) continue;
    (lines as Obj[]).forEach((ln, lni) => {
      const label = str(ln.lineNumber) || str(ln.name) || `Linie ${lni + 1}`;
      const courses = Array.isArray(ln.courses) ? (ln.courses as Obj[]) : [];
      courses.forEach((c, ci) => {
        const pom = Array.isArray(c.pathOnMap) ? (c.pathOnMap as Obj[]) : [];
        const path: [number, number][] = pom
          .map((p) => [num(p.longitude) ?? NaN, num(p.latitude) ?? NaN] as [number, number])
          .filter((xy) => !Number.isNaN(xy[0]) && !Number.isNaN(xy[1]));
        if (path.length < 2) return; // a line of one point is no course
        const headsign = str(c.headsign);
        const nm = headsign ? `${label} → ${headsign}` : label;
        const id = `line-${str(ln.id) ?? label}-${ci}`;
        if (!model.tracks.some((t) => t.id === id)) model.tracks.push({ id, name: nm, path, source: 'line_course', ref: str(ln.id) ?? undefined });
      });
    });
  }

  // 3. journeys — the map groups legs by journey (the recommended one is drawn solid, the others
  //    fade). A round-trip or two-proposal answer makes SEVERAL connections calls, and each result
  //    numbers its own journeys from 0 — so the per-result index is NOT unique across the answer.
  //    Number every journey with one running counter across all results; otherwise a later result's
  //    journey 0 collides with the first result's journey 0, both land under one index, and the map
  //    draws them together — the "the RE is recommended but the ICE is drawn" bug.
  let journeyCounter = 0;
  for (const { name, value } of parsed) {
    if (!/connections/.test(name)) continue;
    const journeys = (value as Obj).journeys;
    if (!Array.isArray(journeys)) continue;
    journeys.forEach((j) => {
      const ji = journeyCounter++;
      const legs = (j as Obj).legs;
      if (!Array.isArray(legs)) return;
      legs.forEach((l, li) => {
        const leg = l as Obj;
        const mot = (leg.mot as Obj) ?? {};
        const line = (mot.line as Obj) ?? {};
        const nodes: MapLegNode[] = (Array.isArray(leg.nodes) ? (leg.nodes as Obj[]) : []).map((n) => {
          let ref = mainId(n) ?? undefined;
          let name = str(n.name) ?? '?';
          const loc = n.location as Obj | undefined;
          let lat = num(loc?.latitude) ?? undefined;
          let lon = num(loc?.longitude) ?? undefined;
          if ((lat === undefined || lon === undefined) && ref) {
            const known = stopsByStation.get(stationKey(ref));
            if (known) {
              lat = known.lat;
              lon = known.lon;
            }
          }
          // a journey asked by coordinates comes back with ends called "Origin" and "Destination"
          // and no id; the answer resolved those coordinates from a real place (resolve_location
          // "Wunstorf" → 52.4223/9.4504), so the end is that place — named and keyed as such
          if (!ref && /^(origin|destination)$/i.test(name) && lat !== undefined && lon !== undefined) {
            const here = { lat, lon };
            const resolved = model.pins.find((p) => metresBetween(p, here) <= 25);
            if (resolved) {
              name = resolved.name;
              ref = resolved.ref;
            }
          }
          const ev = Array.isArray(n.events) && n.events[0] ? (n.events[0] as Obj) : {};
          const departs = instants(ev.scheduledDepartureDateTime, ev.actualDepartureDateTime);
          const arrives = instants(ev.scheduledArrivalDateTime, ev.actualArrivalDateTime);
          return {
            name,
            ref,
            lat,
            lon,
            time: str(ev.scheduledDepartureDateTime) ?? str(ev.scheduledArrivalDateTime) ?? undefined,
            ...(departs.length ? { departs } : {}),
            ...(arrives.length ? { arrives } : {}),
            platform: str((n.platform as Obj | undefined)?.name) ?? undefined,
          };
        });
        const path: [number, number][] = Array.isArray(leg.pathOnMap)
          ? (leg.pathOnMap as Obj[]).map((p) => [num(p.longitude) ?? NaN, num(p.latitude) ?? NaN] as [number, number]).filter((c) => !Number.isNaN(c[0]) && !Number.isNaN(c[1]))
          : [];
        const mode = modeOf(str(mot.type), str(leg.legType));
        // a non-transit ride carries no line number; name it by its mode so a bike leg (`mot.type:
        // bike`, `legType: individual`) reads "Rad", not "Fußweg" — "Fußweg" belongs to a walk.
        const nonTransitLabel: Record<string, string> = { walk: 'Fußweg', bike: 'Rad', car: 'Auto', scooter: 'E-Scooter' };
        const mapLeg: MapLeg = {
          journeyIndex: ji,
          legIndex: li,
          line: str(line.lineNumber) ?? str(line.name) ?? (str(leg.legType) === 'public' ? '?' : (nonTransitLabel[mode] ?? 'Fußweg')),
          mode,
          operator: str((line.operator as Obj | undefined)?.name) ?? undefined,
          direction: str((line.direction as Obj | undefined)?.name) ?? undefined,
          nodes,
          path,
          pathSource: str(leg.pathSource) ?? undefined,
          course: str((leg.additionalInfos as Obj | undefined)?.course) ?? undefined,
          colour: cssColour(str(leg.pathColour)),
        };
        model.legs.push(mapLeg);
      });
    });
  }

  // 3a. a line's whole course (line_course) is no part of an answer about a connection: the
  //     journeys are, and a whole line runs far past where they board and alight
  if (model.legs.length > 0) model.tracks = model.tracks.filter((t) => t.source !== 'line_course');
  // the journey the answer recommends, and what of the journeys the map draws
  model.primaryJourney = pickPrimaryJourney(answerText, model.legs);
  const { route, drawn } = routeOf(model.legs, model.primaryJourney, answerText);
  model.route = route;
  model.drawnJourneys = drawn;

  // 3b. an answer with journeys shows the stops the drawn trip boards, changes and alights at — a
  //     resolved candidate the model did not travel from or to would put a pin on an unrelated town,
  //     and one its bus merely passes ("Hauptbahnhof/ZOB") on a stop nobody gets on or off. A
  //     departures board likewise shows the stop it is for, not the lookup's neighbour
  //     ("Kröpcke/Theaterstraße" next to the Kröpcke board). An answer about a place (tourism POIs
  //     present) drops the lookup's namesakes ("Badeland" in Uelzen, Nortorf and Celle when the
  //     question was about Wolfsburg).
  const aboutPois = model.pins.some((p) => p.kind === 'poi' && !candidates.has(p.id));
  if (model.legs.length > 0 || boardStations.size > 0 || aboutPois) {
    const used = new Set<string>([...boardStations]);
    for (const l of model.route) {
      if (l.alternative) continue;
      for (const n of [l.nodes[0], l.nodes[l.nodes.length - 1]]) if (n?.ref) used.add(stationKey(n.ref));
    }
    model.pins = model.pins.filter((p) => !(candidates.has(p.id) && !(p.kind === 'stop' && p.ref && used.has(stationKey(p.ref)))));
  }

  // 4. everything else: a tool we do not know by name (meta-open-data, future
  //    servers) still gets its named places on the map when they carry coordinates.
  const covered = /^(resolve_location|nearby|stops|search_place|reverse_geocode|connections|departures|link_datetime|get_usage_guide)$/;
  for (const entry of parsed) {
    const { name, value } = entry;
    if (openData.has(entry) || covered.test(name) || /tourism|poi|kg_pois|details/.test(name)) continue;
    walk(value, (o) => {
      const nm = str(o.name) ?? str(o.title);
      const c = coordsOf(o);
      if (!nm || !c) return;
      addPin({ id: `x-${name}-${str(o.id) ?? `${c.lat},${c.lon}`}`, kind: 'poi', name: nm, lat: c.lat, lon: c.lon, address: str(o.address) ?? str(o.street) ?? undefined, description: str(o.type) ?? str(o.category) ?? undefined, source: name });
    });
  }

  // the towns the tourism searches were made for (`place: Bad Harzburg`): a tour's name carries
  // them ("Gestütswiesen-Wanderung Bad Harzburg"), the text's line about it often does not
  const places = parsed.map(({ value }) => (value !== null && typeof value === 'object' && !Array.isArray(value) ? str((value as Obj).place) : null)).filter((v): v is string => !!v);
  model.pins = candidatesNearTheAnswer(model.pins, candidates, model.legs);
  model.pins = pinsTheAnswerTalksAbout(model.pins, answerText);
  model.tracks = toursTheAnswerTalksAbout(model.tracks, answerText);
  // the town's own record ("Oldenburg", a TouristAttraction named like the town the answer looked
  // up) is not a sight among the sights the answer lists — it stays only when it is all there is
  const townRecord = (p: MapPin): boolean => p.kind === 'poi' && townLookups.has(norm(p.name));
  if (model.pins.some((p) => p.kind === 'poi' && !townRecord(p))) model.pins = model.pins.filter((p) => !townRecord(p));
  for (const p of model.pins) {
    const q = quoteFor(p.name, answerText, p.kind === 'poi' ? { ignore: [p.address ?? '', ...places] } : { area: p.address });
    if (q) p.quote = q;
    if (p.facts && p.facts.length === 0) delete p.facts;
    if (p.links && p.links.length === 0) delete p.links;
  }
  model.bbox = bboxOf(model);
  return model;
}

/** The popup says at least what the answer said: the line of the text that speaks about the
 *  place, freed of its list marker and heading marks, cut at ~280 characters on a word.
 *  Preferred is a line that carries the name as a phrase — with its town for a stop ("Hannover
 *  Hbf" for "Hauptbahnhof" in Hannover) — and among those the line that opens with it
 *  ("**Kniestraße** – rund 100 m" over "Rund um Kniestraße 35 gibt es…", where the street is only
 *  part of an address). Failing that, the line that carries every word of the name except the
 *  ones that only say where it is ("Gestütswiesen-Wanderung" for "Gestütswiesen-Wanderung Bad
 *  Harzburg" — `ignore` holds the address and the searched town). */
export function quoteFor(name: string, text: string, context: { area?: string; ignore?: string[] } = {}): string | undefined {
  if (!text) return undefined;
  const lines = text
    .split('\n')
    .map((raw) => raw.replace(/^\s*(?:[-*•]|\d+[.)])\s+/, '').replace(/^#+\s*/, '').trim())
    .filter((l) => l.length >= 4);
  const base = norm(bareName(name));
  if (!base) return undefined;
  const area = context.area ? norm(context.area) : '';
  const phrases = [...(area && !base.includes(area) ? [`${area} ${base}`, `${base} ${area}`] : []), base];
  for (const phrase of phrases) {
    const hits = lines.filter((l) => hasPhrase(norm(l), phrase));
    const best = hits.find((l) => norm(l).startsWith(phrase)) ?? hits[0];
    if (best) return cutQuote(best);
  }
  const ignore = new Set((context.ignore ?? []).flatMap((s) => norm(s).split(' ')));
  const words = wordsOf(name).filter((w) => !ignore.has(w));
  if (words.length === 0) return undefined;
  const line = lines.find((l) => { const h = norm(l); return words.every((w) => wholeWord(w).test(h)); });
  return line ? cutQuote(line) : undefined;
}

const cutQuote = (line: string): string => {
  if (line.length <= 280) return line;
  const cut = line.slice(0, 277);
  return cut.slice(0, cut.lastIndexOf(' ') > 200 ? cut.lastIndexOf(' ') : 277) + '…';
};

/** Units that make a number after a name a measure ("Grenzwanderung im Harz: 23,8 km"), not a
 *  house number ("Allerpark 4", which is an address and no mention of the stop "Allerpark"). */
const UNITS = 'm|km|min|h|std|hm|uhr|€|euro|rad|räder|meter|kilometer|minuten|stunden';

/** The phrase as whole words of the folded text — and, for a name without a number of its own,
 *  not where a house number follows it. */
function hasPhrase(hay: string, phrase: string): boolean {
  const guard = /\d/.test(phrase) ? '' : `(?! \\d{1,3}[a-z]?(?: |$)(?! ?(?:${UNITS})(?: |$)))`;
  return new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(phrase)}${guard}(?![\\p{L}\\p{N}])`, 'u').test(hay);
}

/* ---- facts and links: what a tool object says about a place, in reader's words ---- */

type Fmt = (v: ToonValue) => string | null;
const asText: Fmt = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : null);
const yesNo: Fmt = (v) => (v === true ? 'ja' : v === false ? 'nein' : null);
const metres: Fmt = (v) => { const n = num(v); return n === null ? null : n < 950 ? `${Math.round(n)} m` : `${(n / 1000).toFixed(1).replace('.', ',')} km`; };
const clock: Fmt = (v) => { const s = str(v); if (!s) return null; const d = new Date(s); return Number.isNaN(d.getTime()) ? s : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) + ' Uhr'; };
const list: Fmt = (v) => (Array.isArray(v) ? (v as ToonValue[]).map((x) => str(x)).filter(Boolean).slice(0, 4).join(', ') || null : asText(v));
const KIND: Record<string, string> = { bikeRental: 'Leihrad-Station', bike_rental: 'Leihrad-Station', carSharing: 'Carsharing', car_sharing: 'Carsharing', scooterRental: 'E-Scooter', scooter_rental: 'E-Scooter', cargoBike: 'Lastenrad', TouristAttraction: 'Sehenswürdigkeit', TouristTrip: 'Tour', Tour: 'Tour', LodgingBusiness: 'Unterkunft', FoodEstablishment: 'Gastronomie', Event: 'Veranstaltung', POI: 'Ort' };
const kind: Fmt = (v) => { const s = str(v); return s ? KIND[s] ?? null : null; };
const minutes: Fmt = (v) => { const n = num(v); return n === null ? null : n < 90 ? `${Math.round(n)} Min` : `${(n / 60).toFixed(1).replace('.', ',')} h`; };
// a suitability axis is worth a line when it holds ("kinderwagengeeignet"); "nein" is no fact
const yes: Fmt = (v) => (v === true ? 'ja' : null);
const entry: Fmt = (v) => (v === 'yes' || v === true ? 'kostenpflichtig' : v === 'no' || v === false ? 'frei' : null);
const ages: Fmt = (v) => (Array.isArray(v) ? (v as ToonValue[]).map((x) => str(x)).filter((x) => x && x !== 'general').join(', ') || null : null);
/** How many facts a popup lists at most — enough for a rental station's eight and a tour's axes. */
const MAX_FACTS = 10;
/** The keys a popup lists, in this order; everything else a tool says stays in the text. */
const FACTS: [string, string, Fmt][] = [
  ['vehiclesAvailable', 'Fahrzeuge frei', asText], ['bikesAvailable', 'Räder frei', asText], ['docksAvailable', 'Stellplätze frei', asText],
  ['isRenting', 'Ausleihe', yesNo], ['isReturning', 'Rückgabe', yesNo],
  ['operator', 'Anbieter', asText], ['modalityType', 'Art', kind], ['distanceM', 'Entfernung', metres],
  // a curated tour's axes (get_tourism_details); a missing ascent means "unknown", never "flat"
  ['length_m', 'Länge', metres], ['duration_min', 'Dauer', minutes], ['ascent_m', 'Anstieg', metres],
  ['descent_m', 'Abstieg', metres], ['round_trip', 'Rundtour', yesNo], ['activities', 'Aktivitäten', list],
  // entry (OpenStreetMap `fee`) and the family axes (`suitability`, Sprint 183) — what a text says
  // as "eintrittsfrei", "kinderwagengeeignet", "familiengeeignet"
  ['fee', 'Eintritt', entry], ['family', 'Für Familien', yes], ['pushchair', 'Kinderwagen', yes],
  ['indoor_bad_weather', 'Bei schlechtem Wetter', yes], ['seal', 'Familiensiegel', yes], ['age_bands', 'Alter', ages],
  ['lastReported', 'Stand', clock], ['phone', 'Telefon', asText], ['email', 'E-Mail', asText], ['categories', 'Kategorie', list],
];
const SKIP = new Set(['ids', 'location', 'coord', 'geo', 'media', 'media_objects', 'texts', 'subitems', 'license', 'source', 'attributes', 'features', 'ratings', 'events', 'legs', 'nodes', 'path_on_map']);

function scalarsOf(objs: Obj[]): Map<string, ToonValue> {
  const out = new Map<string, ToonValue>();
  const visit = (o: Obj, depth: number, parent: string): void => {
    for (const [k, v] of Object.entries(o)) {
      if (SKIP.has(k)) continue;
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) { if (depth < 2) visit(v as Obj, depth + 1, k); continue; }
      const key = parent === 'modality' && k === 'type' ? 'modalityType' : k;
      if (!out.has(key)) out.set(key, v);
    }
  };
  for (const o of objs) visit(o, 0, '');
  return out;
}

export function factsOf(objs: Obj[]): PinFact[] {
  const s = scalarsOf(objs);
  const facts: PinFact[] = [];
  for (const [key, label, fmt] of FACTS) {
    if (!s.has(key)) continue;
    const value = fmt(s.get(key)!);
    if (value) facts.push({ label, value });
  }
  return facts.slice(0, MAX_FACTS);
}

const LINKS: [string, string][] = [['appUrl', 'In der App öffnen'], ['booking', 'Buchen'], ['web', 'Website'], ['website', 'Website'], ['url', 'Website'], ['uri', 'Website']];
export function linksOf(objs: Obj[]): PinLink[] {
  const s = scalarsOf(objs);
  const links: PinLink[] = [];
  for (const [key, label] of LINKS) {
    const url = httpsUrl(str(s.get(key) ?? null));
    if (!url || links.some((l) => l.url === url)) continue;
    links.push({ label: /openstreetmap\.org/.test(url) ? 'OpenStreetMap' : label, url });
  }
  return links;
}

/** Metres between two points, close enough for "the same building". */
const metresBetween = (a: { lat: number; lon: number }, b: { lat: number; lon: number }): number =>
  1000 * Math.hypot((a.lat - b.lat) * 111, (a.lon - b.lon) * 111 * Math.cos((a.lat * Math.PI) / 180));

/** Two POI/place records are one place when they carry the same name — or one is the other plus
 *  its town ("Prinzenpalais" from OpenStreetMap, "Prinzenpalais Oldenburg" from the Hub) — within
 *  100 m. Namesakes in two towns are two places. */
function samePlace(a: MapPin, b: MapPin): boolean {
  if (metresBetween(a, b) > 100) return false;
  const na = norm(bareName(a.name)), nb = norm(bareName(b.name));
  if (na === nb) return true;
  const [long, short] = na.length > nb.length ? [na, nb] : [nb, na];
  if (!long.startsWith(`${short} `)) return false;
  const town = long.slice(short.length + 1);
  return [a.address, b.address].some((addr) => !!addr && norm(addr).split(' ').includes(town));
}

/** A lookup's namesakes far from everything else the answer places are disambiguation noise:
 *  "Kniestraße" in Kiel next to ten rental stations in Hannover. When the answer has at least two
 *  places of its own (non-candidates, leg halts), a candidate farther than 50 km from their middle
 *  is dropped — unless a journey starts or ends there. An answer that is nothing but a lookup
 *  keeps every hit. */
export function candidatesNearTheAnswer(pins: MapPin[], candidates: Set<string>, legs: MapLeg[]): MapPin[] {
  // the stop a journey starts or ends at is the answer's own place, however far from the rest
  const used = new Set(legs.flatMap((l) => l.nodes.filter((n) => n.ref).map((n) => stationKey(n.ref!))));
  const anchors: [number, number][] = [
    ...pins.filter((p) => !candidates.has(p.id)).map((p) => [p.lat, p.lon] as [number, number]),
    ...legs.flatMap((l) => l.nodes.filter((n) => n.lat !== undefined && n.lon !== undefined).map((n) => [n.lat!, n.lon!] as [number, number])),
  ];
  // a lookup answers the house AND the street ("Kniestraße 35" and "Kniestraße", same town): the
  // street-level hit is the less specific spelling of the same place and adds only a stray pin
  const cands = pins.filter((p) => candidates.has(p.id));
  const lessSpecific = new Set(cands.filter((a) => cands.some((b) => b !== a && b.address === a.address && b.name.startsWith(a.name + ' '))).map((p) => p.id));
  pins = pins.filter((p) => !lessSpecific.has(p.id));
  if (anchors.length < 2) return pins;
  const mid = (xs: number[]): number => xs.slice().sort((a, b) => a - b)[Math.floor(xs.length / 2)]!;
  const [lat0, lon0] = [mid(anchors.map((a) => a[0])), mid(anchors.map((a) => a[1]))];
  const km = (lat: number, lon: number): number => Math.hypot((lat - lat0) * 111, (lon - lon0) * 111 * Math.cos((lat0 * Math.PI) / 180));
  return pins.filter((p) => !candidates.has(p.id) || (p.ref && used.has(stationKey(p.ref))) || km(p.lat, p.lon) <= 50);
}

/** Decode an encoded polyline (Google's format: lat before lon, deltas, `precision` decimals) to
 *  `[lon, lat]` points. A string cut short or a point off the globe gives nothing: never a guess. */
export function decodePolyline(s: string, precision = 5): [number, number][] {
  const factor = 10 ** precision;
  const out: [number, number][] = [];
  let i = 0;
  let lat = 0;
  let lon = 0;
  while (i < s.length) {
    const d: number[] = [];
    for (let k = 0; k < 2; k++) {
      let result = 0;
      let shift = 0;
      let b: number;
      do {
        if (i >= s.length) return [];
        b = s.charCodeAt(i++) - 63;
        if (b < 0 || b > 63) return [];
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      d.push(result & 1 ? ~(result >> 1) : result >> 1);
    }
    lat += d[0]!;
    lon += d[1]!;
    const y = lat / factor;
    const x = lon / factor;
    if (Math.abs(y) > 90 || Math.abs(x) > 180) return [];
    out.push([x, y]);
  }
  return out;
}

/** A tour search sketches five tours, the answer recommends two: the map draws the two (and fits
 *  to them). Only the Hub's tour tracks are thinned — a line's course is governed elsewhere — and
 *  only when the text names at least one of them (a text that names none keeps every sketch). */
export function toursTheAnswerTalksAbout(tracks: MapTrack[], text: string): MapTrack[] {
  if (!text) return tracks;
  const tours = tracks.filter((t) => t.source === 'niedersachsen-hub' && t.id.startsWith('tour-'));
  if (tours.length < 2) return tracks;
  const hay = norm(text);
  const named = new Set(tours.filter((t) => mentions(t.name, hay)).map((t) => t.id));
  if (named.size === 0) return tracks;
  return tracks.filter((t) => !tours.includes(t) || named.has(t.id));
}

/** A search returns twenty hits, the answer talks about six: the map shows the six. POI pins
 *  whose name the answer text does not mention are dropped — but only when the text names at
 *  least one of them (a text that names none keeps everything: the model may have paraphrased). */
export function pinsTheAnswerTalksAbout(pins: MapPin[], text: string): MapPin[] {
  if (!text) return pins;
  const hay = norm(text);
  // POIs and places (rental stations, addresses, towns) — stops are governed by the journey rule
  const eligible = pins.filter((p) => p.kind !== 'stop');
  if (eligible.length < 2) return pins;
  const named = eligible.filter((p) => mentions(p.name, hay));
  if (named.length === 0) return pins;
  // two records of one trailhead, "Großer Burgberg - Baumwipfelpfad" and "… & BaumSchwebeBahn":
  // when the answer names the fuller one, the record that says the same in fewer words is the
  // same place and not a second pin
  const words = (p: MapPin): string[] => norm(bareName(p.name)).split(' ');
  const lessSpecific = new Set(named.filter((a) => named.some((b) => b !== a && b.kind === a.kind && metresBetween(a, b) <= 100 && words(b).length > words(a).length && words(a).every((w) => words(b).includes(w)))).map((p) => p.id));
  const keep = new Set(named.filter((p) => !lessSpecific.has(p.id)).map((p) => p.id));
  return pins.filter((p) => p.kind === 'stop' || keep.has(p.id));
}

/** The answer rarely repeats a name verbatim ("Heide-Shuttle Ring 1" becomes "Ring 1 · rund 69 km"
 *  under a "Heide-Shuttle" heading), so a name counts as mentioned when each of its words occurs
 *  in the text as a whole word — "Ring", "1" and "Heide-Shuttle" do, "Fußgängermaut" does not. */
export function mentions(name: string, normalisedText: string): boolean {
  const words = wordsOf(name);
  return words.length > 0 && words.every((w) => wholeWord(w).test(normalisedText));
}

/** The words of a name that carry its identity: folded, without the tags after " | " and the
 *  bracketed qualifier, without the filler words that only join the parts. */
function wordsOf(name: string): string[] {
  return norm(bareName(name)).split(' ').filter((t) => !FILLER.has(t) && (t.length >= 2 || /^\d+$/.test(t)));
}

/** A record may carry tags after " | " ("Großer Burgberg - Baumwipfelpfad | Leichte Strecke |
 *  Rundwanderweg") and a bracketed qualifier ("Oldenburg(Oldb) Hbf", "Radau-Wasserfall-Wanderung
 *  (Bad Harzburg)"); the name is what stands before the first " | ", brackets left out. */
const bareName = (name: string): string => name.split(' | ')[0]!.replace(/\([^)]*\)/g, ' ');

/** Words that link the parts of a name without naming anything ("Romkerhaller Wasserfall-Wanderung
 *  bei Bad Harzburg", "Hof an den Teichen"): the text may leave them out. */
const FILLER = new Set(['der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'in', 'im', 'an', 'am', 'bei', 'beim', 'zu', 'zum', 'zur', 'und', 'von', 'vom', 'mit', 'auf', 'für']);

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const wholeWord = (w: string): RegExp => new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(w)}(?![\\p{L}\\p{N}])`, 'u');

/** The spelling both sides of a name match are folded to: lower case, "ß" as "ss" (the hub writes
 *  "Kniestrasse", the answer "Kniestraße"), "Hauptbahnhof" as "Hbf", a dotted abbreviation as one
 *  word ("B.O.C." → "boc", so the shop is not matched by "Celle" alone), a time or a decimal kept
 *  whole ("07:32" is not the house number 32), everything else — markdown marks included — split
 *  on punctuation. */
export const norm = (s: string): string =>
  s
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/\bhauptbahnhof\b/g, 'hbf')
    .replace(/(?<![\p{L}\p{N}])((?:\p{L}\.){2,})/gu, (abbr) => abbr.replace(/\./g, ''))
    .replace(/(\d)[:.,](\d)/g, '$1$2')
    .replace(/[\s\u00a0]+/g, ' ')
    .replace(/[„“"'’·\-–—:,.!?()|*_#`]/g, ' ')
    .replace(/ +/g, ' ')
    .trim();

/**
 * Whether a leg's path is a real track (drawn solid) or a schematic chain of halts (dashed).
 *
 * `geo` always draws the track. Since the switch to papi (04.10.2026) `pathSource: otp` names OUR router,
 * and papi draws most courses on the network itself — it says so in `additionalInfos.course` (`matched`,
 * `shape`, `street` for a walk/drive on the street graph; only `stops` is the chain of halts — seen live
 * 04.10.2026 on the sold facade). The old rule "solid only for geo" drew all of those dashed.
 * Without `course` (another router, an older server) a path with clearly more vertices than the leg has
 * halts is a track; a chain of halts has one vertex per halt, and two points are always a straight line.
 */
export function isRealTrack(leg: Pick<MapLeg, 'path' | 'pathSource' | 'course' | 'nodes'>): boolean {
  if (leg.path.length < 2) return false;
  if (leg.pathSource === 'geo') return true;
  if (leg.course) return leg.course !== 'stops';
  if (!leg.pathSource) return leg.path.length >= 3;
  return leg.path.length > Math.max(leg.nodes.length, 2) + 2;
}

/** The journey the answer recommends is not necessarily the router's first: the model may pick
 *  the second because it is direct, the third because it fits "morgen früh", or one of another
 *  query. The text names its times and lines ("22:31 → 00:30", "Bus 436", "RB 40"), so the journey
 *  whose times and lines the text names is the one the map puts first. Ties and no match at all
 *  fall back to the first transit journey. */
export function pickPrimaryJourney(text: string, legs: MapLeg[]): number {
  // the recommended journey is a TRANSIT journey — a bike/walk leg's "line" ("Rad", "Fußweg") is
  // not a line the text recommends, so it must not decide the pick (else the onward bike ride
  // becomes "primary" and the S-Bahn before it is dropped). Fall back to the first transit journey,
  // not a hardcoded 0, since the transit leg may not be journey 0.
  const transitIdx = [...new Set(legs.filter(isTransit).map((l) => l.journeyIndex))].sort((a, b) => a - b);
  if (transitIdx.length === 0) {
    const any = [...new Set(legs.map((l) => l.journeyIndex))].sort((a, b) => a - b);
    return any.length ? any[0]! : 0;
  }
  const base = transitIdx[0]!;
  const journeys = new Map<number, string[]>();
  for (const l of legs) {
    if (!isTransit(l) || !l.line || l.line === '?') continue;
    journeys.set(l.journeyIndex, [...(journeys.get(l.journeyIndex) ?? []), l.line]);
  }
  if (journeys.size < 2 || !text) return base;
  const clocks = new Map<number, Set<string>>();
  for (const l of legs) {
    const own = clocks.get(l.journeyIndex) ?? new Set<string>();
    for (const n of l.nodes) for (const t of [...(n.departs ?? []), ...(n.arrives ?? [])]) { const c = clockOf(t); if (c) own.add(c); }
    clocks.set(l.journeyIndex, own);
  }
  // What the text says about an alternative does not recommend it. Of the rest, the times it names
  // decide first — a minute pins one journey, a line number many ("Bus 121 … 22:31 → 00:30" is the
  // long journey, not the shorter one of another query whose only line is the 121) — then the lines,
  // a complete mention over a partial one.
  const { main } = splitAlternatives(text);
  let best = base, bestTimes = 0, bestLines = 0;
  for (const [idx, lines] of [...journeys.entries()].sort((a, b) => a[0] - b[0])) {
    const hit = lines.filter((line) => lineMentioned(line, main)).length;
    const lineScore = hit / lines.length + (hit === lines.length ? 1 : 0);
    const times = [...(clocks.get(idx) ?? [])].filter((c) => timeMentioned(c, main)).length;
    if (times > bestTimes || (times === bestTimes && lineScore > bestLines)) { best = idx; bestTimes = times; bestLines = lineScore; }
  }
  return best;
}

/** What may follow a number that makes it a measure or a count, not a line: "380 m", "09:38 Uhr",
 *  "3,80 €", "in 5 Minuten", "nach 3 Haltestellen", "1 Umstieg". */
const MEASURE_OR_COUNT = '(?:m|km|uhr|€)(?!\\p{L})|(?:min|minute|std|stunde|meter|kilometer|euro|haltestelle|halt|station|umstieg|mal)\\p{L}*';

/** Whether the text names a transit line: "RB 40" also as "RB40", "mit dem RE 3." at the end of a
 *  sentence, "S2 mit …" — but not inside a time ("08:38"), a word ("Gleis 1" is no S1) or a measure.
 *  A bare number (Hannover's Stadtbahn runs lines 1–17) is no line where it numbers a platform
 *  ("Gleis 1", "Steig 4") or is joined to a word ("10-Minuten-Takt"). */
function lineMentioned(line: string, text: string): boolean {
  const compact = line.replace(/\s+/g, '');
  if (!compact) return false;
  const pattern = escapeRegExp(compact).replace(/^([A-Za-z]+)(\d)/, '$1 ?$2');
  const bare = /^\d+$/.test(compact);
  const notAfter = bare ? '(?<!(?:gleis|steig|bahnsteig|platz)\\s?)' : '';
  const notBefore = `[\\p{L}\\d:]|[.,]\\d|\\s?(?:${MEASURE_OR_COUNT})${bare ? '|-' : ''}`;
  return new RegExp(`(^|[^\\p{L}\\d:.,])${notAfter}${pattern}(?!${notBefore})`, 'iu').test(text.replace(/\s+/g, ' '));
}

const BERLIN_CLOCK = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

/** "2026-10-28T21:58:00+01:00" → "21:58": the clock a text about Niedersachsen writes. */
function clockOf(instant: string): string | null {
  const d = new Date(instant);
  return Number.isNaN(d.getTime()) ? null : BERLIN_CLOCK.format(d);
}

/** Whether the text names this clock time — "09:08" also as "9:08" — and not as part of a longer number. */
function timeMentioned(clock: string, text: string): boolean {
  const [h, m] = clock.split(':') as [string, string];
  const hour = h.startsWith('0') ? `0?${h[1]}` : h;
  return new RegExp(`(?<![\\d:.,])${hour}:${m}(?!\\d)`).test(text);
}

/** Whether the text names one of these instants by its clock time. */
const told = (instants: string[] | undefined, text: string): boolean =>
  (instants ?? []).some((t) => { const c = clockOf(t); return c !== null && timeMentioned(c, text); });

/** "Alternativen", "Alternative Verbindungen" — the words of a heading over alternatives. */
const ALTERNATIVES = 'alternativ\\p{L}*(?:\\s+[\\p{L}-]+){0,2}';
/** A heading over alternatives: "### Alternativen", "**Alternative Verbindungen:**", "Alternativen:". */
const ALTERNATIVES_HEADING = new RegExp(`^\\s*(?:#{1,6}\\s*${ALTERNATIVES}\\s*:?|\\*\\*\\s*${ALTERNATIVES}\\s*:?\\s*\\*\\*:?|${ALTERNATIVES}\\s*:)\\s*$`, 'iu');
/** Any other heading: a markdown heading or a bold line of its own. */
const HEADING = /^\s*(?:#{1,6}\s+\S|\*\*[^*]+\*\*:?\s*$)/;

/** The text in two parts: what it says about alternatives and the rest, the recommendation. About
 *  alternatives is the paragraph under an alternatives heading (also when a blank line follows the
 *  heading), and in any line the sentence that says "alternativ…" with the sentences after it. */
function splitAlternatives(text: string): { main: string; alternatives: string } {
  const main: string[] = [];
  const alternatives: string[] = [];
  let section: 'none' | 'headed' | 'open' = 'none';
  for (const line of text.split('\n')) {
    if (ALTERNATIVES_HEADING.test(line)) { section = 'headed'; alternatives.push(line); continue; }
    if (!line.trim()) { if (section === 'open') section = 'none'; continue; }
    section = section === 'headed' ? 'open' : section === 'open' && HEADING.test(line) ? 'none' : section;
    let aboutAlternatives = section === 'open';
    for (const sentence of line.split(/(?<=[.!?])\s+/)) {
      aboutAlternatives ||= /alternativ/i.test(sentence);
      (aboutAlternatives ? alternatives : main).push(sentence);
    }
  }
  return { main: main.join('\n'), alternatives: alternatives.join('\n') };
}

/** Which journeys the map draws: the recommended journey and ONLY its continuation SEGMENTS —
 *  the onward legs of the SAME trip — never the router's alternatives. An answer that plans
 *  "S-Bahn to Wunstorf, then bike on to Steinhude" comes back as several connections calls; the
 *  onward bike ride carries no transit line, so it is never the recommended one and used to be
 *  dropped. But the model also asks for ALTERNATIVES (a second, third, fourth train for the same
 *  A→B) — drawing all of those is the "spiderweb" (Stakeholder 2026-09-11). The distinction is
 *  CHAINING, not endpoint-sharing: a continuation STARTS where the recommended trip ENDED
 *  (Wunstorf); an alternative ends at the same place but does not begin there. So keep the
 *  recommended journey and add ONLY the non-transit segments (bike/walk) that chain onto it
 *  end-to-start, in EITHER direction (the S-Bahn ends where the bike begins; the bike may equally
 *  be the recommended journey and the S-Bahn its lead-in). A second TRAIN is never auto-added —
 *  that is an alternative, the spiderweb. Endpoints at ~2 km, repeated to closure. */
export function journeysToDraw(legs: MapLeg[], primary: number): number[] {
  const ends = new Map<number, { o: [number, number]; d: [number, number] }>();
  for (const l of legs) {
    let pts = l.nodes.filter((n) => n.lat !== undefined && n.lon !== undefined).map((n) => [n.lat!, n.lon!] as [number, number]);
    // a rental-bike leg's nodes ("Fahrrad", the destination) often carry NO coordinate — the leg's
    // geometry rides in `path` (pathOnMap) instead. Without this fallback that leg has no endpoints,
    // never chains onto the trip, and silently drops off the map though its line is drawable.
    if (pts.length === 0 && Array.isArray(l.path) && l.path.length >= 2) {
      const a = l.path[0]!, b = l.path[l.path.length - 1]!; // path is [lon, lat] → store [lat, lon]
      pts = [[a[1], a[0]], [b[1], b[0]]];
    }
    if (pts.length === 0) continue;
    const cur = ends.get(l.journeyIndex);
    if (!cur) ends.set(l.journeyIndex, { o: pts[0]!, d: pts[pts.length - 1]! });
    else cur.d = pts[pts.length - 1]!; // extend the journey's end as later legs arrive
  }
  if (!ends.has(primary)) return [primary];
  // which journeys carry transit (a train/tram/bus leg) vs are pure bike/walk continuations
  const transit = new Set(legs.filter(isTransit).map((l) => l.journeyIndex));
  const km = (a: [number, number], b: [number, number]): number =>
    Math.hypot((a[0] - b[0]) * 111, (a[1] - b[1]) * 111 * Math.cos((a[0] * Math.PI) / 180));
  const near = (a: [number, number], b: [number, number]): boolean => km(a, b) <= 2;
  // Start from the recommended journey; add ONLY non-transit segments (bike/walk) that chain onto
  // it — in EITHER direction (the S-Bahn ends where the bike begins, but the bike may also be the
  // primary and the S-Bahn its lead-in). A transit journey is never auto-added: a second train is
  // an alternative, not part of this trip, and drawing it is the spiderweb. Repeat to closure so a
  // walk→bike→walk chain is followed whole.
  const drawn = new Set<number>([primary]);
  for (let changed = true; changed; ) {
    changed = false;
    for (const [idx, e] of ends) {
      if (drawn.has(idx) || transit.has(idx)) continue;
      for (const di of drawn) {
        const D = ends.get(di)!;
        if (near(e.o, D.d) || near(e.d, D.o)) { drawn.add(idx); changed = true; break; }
      }
    }
  }
  return [...drawn].sort((a, b) => a - b);
}

/** How far the first or last point of a leg's course may lie from its boarding or alighting halt
 *  before the course counts as longer than the leg: about a station's extent — platforms, forecourt,
 *  the bus stop in front — the distance the drift report takes for "one place", too. */
export const PATH_END_TOLERANCE_M = 300;

/** What the map draws of the answer's journeys: the recommended journey as the text tells it
 *  (`toldTrip`), the continuation segments that chain onto that told trip (`journeysToDraw`, whole),
 *  then — faint — the alternatives the text names as such (`namedAlternatives`). No other journey
 *  of any query: the router's alternatives and the journeys of queries the answer did not use stay
 *  off the map. Every course runs from its leg's boarding to its alighting (`fitCourse`). `drawn`
 *  lists the journeys of the trip, the recommended one included. */
function routeOf(legs: MapLeg[], primary: number, text: string): { route: RouteLeg[]; drawn: number[] } {
  const { main, alternatives } = splitAlternatives(text);
  const told = toldTrip(legs.filter((l) => l.journeyIndex === primary), main);
  const drawn = journeysToDraw([...told, ...legs.filter((l) => l.journeyIndex !== primary)], primary);
  const trip = drawn.flatMap((j) => (j === primary ? told : legs.filter((l) => l.journeyIndex === j)));
  const faint = namedAlternatives(legs, primary, new Set(drawn), alternatives);
  const route = [...trip.map((l) => ({ ...fitCourse(l), alternative: false })), ...faint.map((l) => ({ ...fitCourse(l), alternative: true }))];
  return { route, drawn };
}

/** The legs of the recommended journey the text tells. The router's journey is where it starts, but
 *  an answer may tell a shorter trip out of it: the reported Nordstadt turn (28.10.2026) takes the S2
 *  at Hannover Hbf — an intermediate halt of an S2 the router boards at Bismarckstraße after a bus —
 *  and walks where the router sends the bus 121 round a loop. So:
 *  - the rides from the first the text refers to — by its line, its kind of vehicle ("Bus",
 *    "S-Bahn"), its departure time or both its halts — to the last; a text that refers to none of
 *    them keeps them all — there is nothing to go by;
 *  - the first of them boards, the last alights where the text says: at a halt it names, with that
 *    halt's time (`boardWhereTold`, `alightWhereTold`);
 *  - a walk when both its ends are on that trip: an end of a ride kept, the journey's start or goal,
 *    or a place the text names.
 *  A text that tells the whole journey gets the whole journey. */
function toldTrip(legs: MapLeg[], text: string): MapLeg[] {
  const hay = norm(text);
  const rides = legs.flatMap((l, i) => (isTransit(l) ? [i] : []));
  const referred = rides.filter((i) => refersTo(legs[i]!, text, hay));
  if (referred.length === 0) return legs;
  const first = referred[0]!, last = referred[referred.length - 1]!;
  const kept = new Map(rides.filter((i) => i >= first && i <= last).map((i) => [i, legs[i]!]));
  kept.set(first, boardWhereTold(kept.get(first)!, text));
  kept.set(last, alightWhereTold(kept.get(last)!, text));
  if (kept.size === rides.length && [...kept].every(([i, l]) => l === legs[i])) return legs;

  const goal = legs[legs.length - 1]!.nodes;
  const anchors = [...[...kept.values()].flatMap((l) => [l.nodes[0], l.nodes[l.nodes.length - 1]]), legs[0]!.nodes[0], goal[goal.length - 1]]
    .filter((n): n is MapLegNode => !!n);
  const onTrip = (n: MapLegNode | undefined): boolean => !!n && (anchors.some((a) => sameHalt(n, a)) || mentions(n.name, hay));
  return legs.flatMap((l, i) => {
    if (isTransit(l)) return kept.has(i) ? [kept.get(i)!] : [];
    return onTrip(l.nodes[0]) && onTrip(l.nodes[l.nodes.length - 1]) ? [l] : [];
  });
}

/** The words a text uses for a ride instead of its line ("weiter mit dem Bus", "über S-Bahn"), by
 *  mode, each from the start of a word ("Busabschnitte") — but no "Busbahnhof", and the rail words
 *  only as words of their own ("Zug", not "Zugang"; "Bahn", not "Bahnhof" or the "Bahn" of "S-Bahn"). */
const RAIL_WORDS = /(?<![\p{L}-])(?:zug|züge|regionalzug|regionalbahn|regionalexpress|fernzug|bahn)(?!\p{L})/iu;
const BUS_WORDS = /(?<!\p{L})bus(?!bahnhof)/iu;
const TRAM_WORDS = /(?<!\p{L})(?:stadtbahn|straßenbahn|strassenbahn|tram)/iu;
const KIND_WORDS: Record<string, RegExp> = {
  bus: BUS_WORDS, coach: BUS_WORDS, trolleybus: BUS_WORDS,
  train: RAIL_WORDS, rail: RAIL_WORDS,
  tram: TRAM_WORDS, lightrail: TRAM_WORDS, subway: /(?<!\p{L})(?:stadtbahn|u-?bahn)/iu,
  ferry: /(?<!\p{L})(?:fähre|schiff)/iu,
};
const S_BAHN_WORDS = /(?<!\p{L})s-?bahn/iu;

/** Whether the text refers to this ride by its kind of vehicle; an S-Bahn line ("S2") by "S-Bahn". */
function kindMentioned(leg: MapLeg, text: string): boolean {
  const words = /^S\s?\d/i.test(leg.line) ? S_BAHN_WORDS : KIND_WORDS[leg.mode.toLowerCase()];
  return !!words && words.test(text);
}

/** Whether the text refers to this ride: by its line, its kind of vehicle, the time it leaves, or the
 *  names of both the halt it leaves and the halt it reaches ("ab Altstadt bis Bahnhof"). `hay` is
 *  the text folded by `norm`. */
function refersTo(leg: MapLeg, text: string, hay: string): boolean {
  const [from, to] = [leg.nodes[0], leg.nodes[leg.nodes.length - 1]];
  return lineMentioned(leg.line, text) || kindMentioned(leg, text) || told(from?.departs, text)
    || (!!from && !!to && mentions(from.name, hay) && mentions(to.name, hay));
}

/** A ride boarded where the text boards it: at an intermediate halt the text names together with
 *  its departure time — unless the text names the time the ride leaves its first halt. */
function boardWhereTold(leg: MapLeg, text: string): MapLeg {
  if (told(leg.nodes[0]?.departs, text)) return leg;
  const hay = norm(text);
  const at = leg.nodes.findIndex((n, i) => i > 0 && i < leg.nodes.length - 1 && told(n.departs, text) && mentions(n.name, hay));
  return at > 0 ? { ...leg, nodes: leg.nodes.slice(at) } : leg;
}

/** A ride alighted where the text alights: at an intermediate halt it names with its arrival time —
 *  unless the text names the time the ride reaches its last halt. */
function alightWhereTold(leg: MapLeg, text: string): MapLeg {
  const lastHalt = leg.nodes.length - 1;
  if (told(leg.nodes[lastHalt]?.arrives, text)) return leg;
  const hay = norm(text);
  for (let i = lastHalt - 1; i > 0; i--) {
    const n = leg.nodes[i]!;
    if (told(n.arrives, text) && mentions(n.name, hay)) return { ...leg, nodes: leg.nodes.slice(0, i + 1) };
  }
  return leg;
}

/** Two halts of one journey are the same place: one station, or within a few metres — or, where a
 *  coordinate is missing to tell, one name. */
function sameHalt(a: MapLegNode, b: MapLegNode): boolean {
  if (a.ref && b.ref && stationKey(a.ref) === stationKey(b.ref)) return true;
  if (placed(a) && placed(b)) return metresBetween({ lat: a.lat!, lon: a.lon! }, { lat: b.lat!, lon: b.lon! }) <= 50;
  return !!norm(a.name) && norm(a.name) === norm(b.name);
}

const placed = (n: MapLegNode | undefined): boolean => n?.lat !== undefined && n.lon !== undefined;

/** The journeys the text names as alternatives: not drawn as the trip, every line of them named in
 *  what the text says about alternatives, and the time their first ride leaves named there too — a
 *  line alone would draw every journey of it ("RE 3, 09:13–09:39" is not the RE 3 at 09:40). One
 *  drawing per alternative, however many queries returned it. */
function namedAlternatives(legs: MapLeg[], primary: number, drawn: Set<number>, text: string): MapLeg[] {
  if (!text) return [];
  const journeys = new Map<number, MapLeg[]>();
  for (const l of legs) journeys.set(l.journeyIndex, [...(journeys.get(l.journeyIndex) ?? []), l]);
  const signature = (own: MapLeg[]): string => own.filter(isTransit).map((r) => `${r.line}@${r.nodes[0]?.departs?.[0] ?? ''}`).join('|');
  const seen = new Set([signature(journeys.get(primary) ?? [])]);
  const out: MapLeg[] = [];
  for (const [j, own] of journeys) {
    const rides = own.filter(isTransit);
    if (drawn.has(j) || rides.length === 0) continue;
    if (!rides.every((r) => r.line !== '?' && lineMentioned(r.line, text)) || !told(rides[0]!.nodes[0]?.departs, text)) continue;
    const key = signature(own);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(...own);
  }
  return out;
}

/** A course runs from its leg's boarding to its alighting. One whose first or last point lies farther
 *  than PATH_END_TOLERANCE_M from them — a ride boarded at an intermediate halt, a server that sent a
 *  longer course — is cut where it passes them, following the halts along the course in their order
 *  (a ring passes a halt twice; the alighting is the pass after the halts before it). Cut, never
 *  extended or moved; an end whose halt has no coordinate stays as it is; when nothing of the course
 *  lies between the two, the map draws the chain of halts instead. */
function fitCourse(leg: MapLeg): MapLeg {
  const path = leg.path;
  const halts = leg.nodes.filter(placed);
  if (path.length < 2 || halts.length < 2) return leg;
  const [boarding, alighting] = [leg.nodes[0]!, leg.nodes[leg.nodes.length - 1]!];
  const last = path.length - 1;
  const far = (i: number, n: MapLegNode): boolean => metresBetween(vertex(path[i]!), halt(n)) > PATH_END_TOLERANCE_M;
  const start = placed(boarding) && far(0, boarding) ? pass(path, boarding, 0) : 0;
  const end = placed(alighting) && far(last, alighting) ? halts.slice(placed(boarding) ? 1 : 0).reduce((at, n) => pass(path, n, at), start) : last;
  if (start === 0 && end === last) return leg;
  return { ...leg, path: end > start ? path.slice(start, end + 1) : [] };
}

const vertex = ([lon, lat]: [number, number]): { lat: number; lon: number } => ({ lat, lon });
const halt = (n: MapLegNode): { lat: number; lon: number } => ({ lat: n.lat!, lon: n.lon! });

/** Where a course, from its point `from` on, passes a halt: the nearest point of the first stretch
 *  within PATH_END_TOLERANCE_M of it — or, when it never comes that close, its nearest point at all. */
function pass(path: [number, number][], n: MapLegNode, from: number): number {
  const off = (i: number): number => metresBetween(vertex(path[i]!), halt(n));
  let i = from;
  while (i < path.length && off(i) > PATH_END_TOLERANCE_M) i++;
  if (i === path.length) {
    let nearest = from;
    for (let j = from; j < path.length; j++) if (off(j) < off(nearest)) nearest = j;
    return nearest;
  }
  while (i + 1 < path.length && off(i + 1) < off(i)) i++;
  return i;
}

/** The legs the map draws — the model's `route`; for a model stored by an older bundle, which has
 *  none, its drawn journeys whole, as that bundle drew them. */
export function drawnRoute(model: MapModel): RouteLeg[] {
  const legs = model.legs ?? [];
  if (model.route?.length || legs.length === 0) return model.route ?? [];
  const drawn = new Set(model.drawnJourneys?.length ? model.drawnJourneys : [model.primaryJourney]);
  return legs.filter((l) => drawn.has(l.journeyIndex)).map((l) => ({ ...l, alternative: false }));
}

/** The mode the map styles by; every spelling of "on foot" (mot.type `walking`, legType
 *  `transition`/`walk`) becomes `walk`, so a footpath is never labelled or drawn like a line. */
function modeOf(motType: string | null, legType: string | null): string {
  const t = (motType ?? '').toLowerCase();
  if (/^(walk|walking|foot|footpath|transfer|transition)$/.test(t)) return 'walk';
  // an individual ride is its own mode, drawn solid like a line, not dotted like a footpath
  if (/^(bike|bicycle|cycle|e-?bike|pedelec)$/.test(t)) return 'bike';
  if (/^(car|auto|taxi)$/.test(t)) return 'car';
  if (/^(scooter|kick_?scooter|e-?scooter)$/.test(t)) return 'scooter';
  if (t) return t;
  return legType === 'public' ? 'transit' : 'walk';
}

/** A coordinate pair in any of the spellings the sources use. */
function coordsOf(o: Obj): { lat: number; lon: number } | null {
  const coord = o.coord as Obj | null | undefined;
  const loc = o.location as Obj | null | undefined;
  const geo = o.geo as Obj | null | undefined;
  const cands: [ToonValue | undefined, ToonValue | undefined][] = [
    [coord?.lat, coord?.lon], [loc?.latitude, loc?.longitude], [loc?.lat, loc?.lon],
    [geo?.latitude, geo?.longitude], [(geo?.main as Obj | undefined)?.latitude, (geo?.main as Obj | undefined)?.longitude],
    [o.lat, o.lon], [o.latitude, o.longitude],
  ];
  for (const [a, b] of cands) {
    const lat = num(a);
    const lon = num(b);
    if (lat !== null && lon !== null && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) return { lat, lon };
  }
  return null;
}

/** "53.11889 9.787323 53.11742 9.789515 …" → [lon, lat][]; a malformed pair ends the course. */
function parseLatLonString(s: string): [number, number][] {
  const nums = s.trim().split(/\s+/).map(Number);
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const lat = nums[i]!, lon = nums[i + 1]!;
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) break;
    out.push([lon, lat]);
  }
  return out;
}

/** The instants a halt's event gives for one direction — the timetable's and the live one — once each. */
function instants(...values: (ToonValue | undefined)[]): string[] {
  return [...new Set(values.map((v) => str(v)).filter((v): v is string => !!v))];
}

/** A colour goes into the stylesheet, so only the plain hex form is accepted. */
function cssColour(v: string | null): string | undefined {
  return v && /^#[0-9a-fA-F]{6}$/.test(v) ? v : undefined;
}

/** Only an absolute https link is worth a click; anything else stays off the popup. */
function httpsUrl(v: string | null): string | undefined {
  return v && /^https:\/\/[^\s"'<>]+$/.test(v) ? v : undefined;
}

function shortName(toolName: string): string {
  return toolName.replace(/^tmn-(mobility|tourism)-mcp_/, '').replace(/^meta-open-data_/, '').replace(/^show_/, '');
}

function isStopNode(o: Obj): boolean {
  return o.type === 'stop' && typeof o.location === 'object' && o.location !== null;
}

function mainId(o: Obj): string | null {
  const ids = o.ids;
  if (Array.isArray(ids)) {
    const main = (ids as Obj[]).find((i) => i.type === 'main') ?? (ids as Obj[])[0];
    const v = main ? str(main.value) : null;
    if (v) return v;
  }
  return str(o.id);
}

export function bboxOf(m: MapModel): MapModel['bbox'] {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  const add = (lon: number, lat: number): void => {
    minLon = Math.min(minLon, lon); maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
  };
  for (const p of m.pins) add(p.lon, p.lat);
  for (const l of m.legs) {
    for (const n of l.nodes) if (n.lat !== undefined && n.lon !== undefined) add(n.lon, n.lat);
    for (const [lon, lat] of l.path) add(lon, lat);
  }
  for (const t of m.tracks ?? []) for (const [lon, lat] of t.path) add(lon, lat);
  return Number.isFinite(minLon) ? [minLon, minLat, maxLon, maxLat] : null;
}

/** The boarding, transfer and alighting stops of the drawn trip that are not already pins of
 *  the answer's own — one per STATION: a transfer arrives at platform 10 and leaves from platform 2,
 *  the walk into the station starts at its centroid, and none of that is a second "Hbf". A stop the
 *  answer resolved (a pin in `pins`) is not pinned again. Intermediate halts are not pins; the map
 *  draws them as dots. Shared by the map and by the drift report, so both see the same pins. */
export function journeyPins(model: MapModel): MapPin[] {
  const atCoordinate = (n: { lat?: number; lon?: number }): string => `c:${n.lat!.toFixed(4)},${n.lon!.toFixed(4)}`;
  const taken = new Set<string>();
  for (const p of model.pins) {
    taken.add(atCoordinate(p));
    if (p.kind === 'stop' && p.ref) taken.add(`s:${stationKey(p.ref)}`);
  }
  const out: MapPin[] = [];
  for (const leg of drawnRoute(model)) {
    if (leg.alternative) continue;
    const nodes = leg.nodes.filter((n) => n.lat !== undefined && n.lon !== undefined);
    for (const n of new Set([nodes[0], nodes[nodes.length - 1]])) {
      if (!n) continue;
      const keys = n.ref ? [`s:${stationKey(n.ref)}`, atCoordinate(n)] : [atCoordinate(n)];
      if (keys.some((k) => taken.has(k))) continue;
      for (const k of keys) taken.add(k);
      out.push({ id: `n-${keys[0]}`, kind: 'stop', name: n.name, lat: n.lat!, lon: n.lon!, address: n.platform ? `Gleis/Steig ${n.platform}` : undefined, ref: n.ref });
    }
  }
  return out;
}

/** What an answer puts on the map, as its badge names it: the trip the text tells (any drawn section
 *  with a coordinate), the tracks, the places. */
export const mapContents = (m: MapModel): { route: boolean; tracks: number; places: number } => ({
  route: drawnRoute(m).some((l) => l.nodes.some((n) => n.lat !== undefined)),
  tracks: (m.tracks ?? []).length,
  places: (m.pins ?? []).length,
});

/** Whether the map has anything to show for the answer: a pin, a track or a drawn section with a coordinate. */
export const hasContent = (m: MapModel | undefined): boolean => {
  if (!m) return false;
  const { route, tracks, places } = mapContents(m);
  return route || tracks > 0 || places > 0;
};
